import { writeLaunchCache } from "~/lib/launch-cache"
import { useEffect, useRef } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AppShellHeader } from "~/components/AppShellHeader"
import { TutorialProvider } from "~/components/tutorials/TutorialProvider"
import { RouteTabs } from "~/components/RouteTabs"
import {
	Workspace,
	WorkspaceOutlet,
	type WorkspaceTab
} from "~/components/workspace/Workspace"
import { EmptyState, LoadingState } from "~/components/screens/StateScreen"
import { OrbitSidebar } from "~/components/sidebar/Sidebar"
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { PermissionPreviewProvider } from "~/hooks/usePermissionPreview"
import { useTeam } from "~/hooks/useTeam"
import { authClient } from "~/lib/auth-client"
import { OrgPermissions, TeamPermissions } from "~/lib/permissions"
import { api } from "~/trpc/react"
import { fancyIssueId } from "~/lib/utils"

function scheduleIdleWork(work: () => void) {
	if (typeof window === "undefined") return () => undefined
	if ("requestIdleCallback" in window) {
		const id = window.requestIdleCallback(work, { timeout: 4000 })
		return () => window.cancelIdleCallback(id)
	}
	const id = globalThis.setTimeout(work, 1200)
	return () => globalThis.clearTimeout(id)
}

function preloadSettingsRouteModules() {
	void Promise.allSettled([
		import("./user-settings"),
		import("./org-settings"),
		import("./team-settings")
	])
}

function usePostMainSettingsPreload() {
	const { organization, isLoading: isLoadingOrg } = useOrganization()
	const { team, isLoading: isLoadingTeam } = useTeam()
	const { hasOrgPermission, hasTeamPermission } = usePermission()
	const utils = api.useUtils()
	const preloadedFor = useRef<string | undefined>(undefined)

	useEffect(() => {
		if (isLoadingOrg || isLoadingTeam || !organization) return

		const preloadKey = `${organization.id}:${team?.id ?? "org"}`
		if (preloadedFor.current === preloadKey) return
		preloadedFor.current = preloadKey

		return scheduleIdleWork(() => {
			preloadSettingsRouteModules()

			const prefetchOptions = { retry: false }
			const prefetches: Promise<unknown>[] = [
				utils.user.getOrgs.prefetch(undefined, prefetchOptions),
				utils.meta.accountLinkingProviders.prefetch(undefined, prefetchOptions),
				utils.notifications.getPreferences.prefetch(undefined, prefetchOptions)
			]

			if (hasOrgPermission(OrgPermissions.ADMINISTRATOR)) {
				prefetches.push(
					utils.roles.getOrgRoles.prefetch(
						{
							organizationId: organization.id
						},
						prefetchOptions
					)
				)
			}

			if (hasOrgPermission(OrgPermissions.VIEW_AUDIT_LOG)) {
				prefetches.push(
					utils.audit.organization.prefetch(
						{
							organizationId: organization.id,
							limit: 100
						},
						prefetchOptions
					)
				)
			}

			if (team) {
				prefetches.push(
					utils.labels.list.prefetch({ teamId: team.id }, prefetchOptions)
				)

				if (hasTeamPermission(TeamPermissions.ADMINISTRATOR)) {
					prefetches.push(
						utils.roles.getTeamRoles.prefetch(
							{ teamId: team.id },
							prefetchOptions
						)
					)
				}

				if (hasTeamPermission(TeamPermissions.VIEW_AUDIT_LOG)) {
					prefetches.push(
						utils.audit.team.prefetch(
							{
								organizationId: organization.id,
								teamId: team.id,
								limit: 100
							},
							prefetchOptions
						)
					)
				}

				if (hasTeamPermission(TeamPermissions.MANAGE_TEAM)) {
					prefetches.push(
						utils.discord.list.prefetch({ teamId: team.id }, prefetchOptions)
					)

					if (team.suggestionsFeatureEnabled) {
						prefetches.push(
							utils.suggestions.getBoard.prefetch(
								{ teamId: team.id },
								prefetchOptions
							)
						)
					}

					if (organization.githubInstallationId) {
						prefetches.push(
							utils.github.listRepositories.prefetch(
								{
									organizationId: organization.id,
									teamId: team.id
								},
								prefetchOptions
							)
						)
					}
				}
			}

			void Promise.allSettled(prefetches)
		})
	}, [
		organization,
		team,
		isLoadingOrg,
		isLoadingTeam,
		hasOrgPermission,
		hasTeamPermission,
		utils
	])
}

export default function Layout() {
	const { organization, isLoading } = useOrganization()
	const { team, isLoading: isTeamLoading, canAccess } = useTeam()
	const {
		orgSlug = "",
		teamSlug,
		issueFriendlyId,
		documentId
	} = useParams<{
		orgSlug: string
		teamSlug?: string
		issueFriendlyId?: string
		documentId?: string
	}>()
	const navigate = useNavigate()
	const location = useLocation()

	const { data: session, isPending: isSessionPending } = authClient.useSession()
	const issue = api.issues.get.useQuery(
		{ friendlyId: issueFriendlyId ?? "", teamId: team?.id ?? "" },
		{
			enabled:
				!!session &&
				!!team?.enableIssues &&
				!!issueFriendlyId &&
				canAccess === true
		}
	)
	const document = api.knowledgeBase.get.useQuery(
		{ documentId: documentId ?? "", teamId: team?.id ?? "" },
		{
			enabled:
				!!session &&
				!!team?.enableKnowledgeBase &&
				!!documentId &&
				canAccess === true
		}
	)
	const visitTeam = api.user.recordTeamVisit.useMutation()
	const visitOrganization = api.user.recordOrganizationVisit.useMutation()
	const lastTrackedTeam = useRef<string | null>(null)
	const lastTrackedOrg = useRef<string | null>(null)
	useEffect(() => {
		if (!organization || !session) return
		if (team && canAccess && lastTrackedTeam.current !== team.id) {
			lastTrackedTeam.current = team.id
			lastTrackedOrg.current = organization.id
			visitTeam.mutate(
				{ teamId: team.id },
				{
					onSuccess: ({ launchPath }) =>
						writeLaunchCache(session.user.id, launchPath)
				}
			)
		} else if (!teamSlug && lastTrackedOrg.current !== organization.id) {
			lastTrackedOrg.current = organization.id
			visitOrganization.mutate(
				{ organizationId: organization.id },
				{
					onSuccess: ({ launchPath }) =>
						writeLaunchCache(session.user.id, launchPath)
				}
			)
		}
		if (!teamSlug) lastTrackedTeam.current = null
	}, [
		organization,
		team,
		canAccess,
		teamSlug,
		session,
		visitTeam.mutate,
		visitOrganization.mutate
	])
	const currentPage: Omit<WorkspaceTab, "id" | "path"> =
		issueFriendlyId && issue.data && team
			? {
					kind: "issue",
					title: `${fancyIssueId(issue.data.friendlyId, team.slug)} · ${issue.data.title}`
				}
			: documentId && document.data
				? {
						kind: "document",
						title: document.data.title
					}
				: {
						kind: "page",
						title: location.pathname.includes("/me/onboarding")
							? "Orbit tour"
							: location.pathname.includes("/me/inbox")
								? "My Inbox"
								: location.pathname.includes("/me/issues")
									? "My Issues"
									: location.pathname.includes("/settings")
										? "Settings"
										: location.pathname.split("/")[3] === "forms"
											? `${team?.name ?? "Team"} · Forms`
											: location.pathname.includes("/kb")
												? `${team?.name ?? "Team"} · Knowledge Base`
												: location.pathname.includes("/projects")
													? "Projects"
													: location.pathname.includes("/automations")
														? "Automations"
														: location.pathname.includes("/suggestions")
															? "Suggestions"
															: location.pathname.endsWith("/home")
																? `${team?.name ?? "Team"} · Home`
																: location.pathname.endsWith("/queue")
																	? `${team?.name ?? "Team"} · Queue`
																	: issueFriendlyId || documentId
																		? "Loading…"
																		: `${team?.name ?? "My"} · Issues`
					}
	usePostMainSettingsPreload()

	useEffect(() => {
		if (!session && !isSessionPending) {
			navigate("/", { replace: true })
			return
		}

		if (!isLoading && !organization) {
			navigate("/", { replace: true })
			return
		}

		if (teamSlug && !isTeamLoading && organization && !team) {
			navigate(`/${organization.slug}`, { replace: true })
			return
		}

		if (
			teamSlug &&
			!isTeamLoading &&
			organization &&
			team &&
			canAccess === false
		) {
			navigate(`/${organization.slug}`, { replace: true })
		}
	}, [
		session,
		isSessionPending,
		isLoading,
		organization,
		isTeamLoading,
		team,
		canAccess,
		teamSlug,
		navigate
	])

	if (isLoading) {
		return <LoadingState className="h-screen w-screen" />
	}

	const teamPage = teamSlug
		? location.pathname.slice(`/${orgSlug}/${teamSlug}/`.length).split("/")[0]
		: undefined
	const disabledFeature =
		team &&
		((teamPage === "home" && !team.enableHome ? "Home" : null) ??
			((issueFriendlyId || teamPage === "issues") && !team.enableIssues
				? "Issues"
				: null) ??
			(teamPage === "queue" && (!team.enableIssues || !team.enableQueue)
				? "Queue"
				: null) ??
			(teamPage === "projects" && !team.enableProjects ? "Projects" : null) ??
			(teamPage === "suggestions" && !team.suggestionsEnabled
				? "Suggestions"
				: null) ??
			(teamPage === "automations" && !team.automationsFeatureEnabled
				? "Automations"
				: null) ??
			(teamPage === "kb" && !team.enableKnowledgeBase
				? "Knowledge Base"
				: null))

	return (
		<Workspace
			key={`${session?.user.id}:${orgSlug}`}
			userId={session?.user.id ?? ""}
			orgSlug={orgSlug}
			homePath={
				teamSlug && team?.enableHome
					? `/${orgSlug}/${teamSlug}/home`
					: `/${orgSlug}/me/issues`
			}
			page={currentPage}
		>
			<PermissionPreviewProvider key={`${session?.user.id}:${orgSlug}`}>
				<TutorialProvider>
					<SidebarProvider className="orbit-app-shell h-dvh min-h-0 flex-col overflow-hidden bg-background">
						<AppShellHeader>
							<RouteTabs />
						</AppShellHeader>
						<div className="relative flex min-h-0 flex-1 gap-2 px-2 pb-2 md:pl-0">
							<OrbitSidebar position="absolute" className="shrink-0" />
							<SidebarInset className="min-h-0 min-w-0 overflow-hidden rounded-xl border border-stroke bg-background shadow-sm">
								{disabledFeature ? (
									<EmptyState
										title={`${disabledFeature} is disabled`}
										description="A team manager can enable this feature in General settings."
									/>
								) : (
									<WorkspaceOutlet />
								)}
							</SidebarInset>
						</div>
					</SidebarProvider>
				</TutorialProvider>
			</PermissionPreviewProvider>
		</Workspace>
	)
}
